import React, { useEffect, useMemo, useState } from 'react'
import { Box, Typography, styled } from '@mui/material'
import { shallow } from 'zustand/shallow'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Card, ChipToggleGroup, EmptyState, Skeleton } from '@/design-system'
import useAppStore from '@/store/useAppStore'
import { useGetConnectRequests, useGetMyCircles } from '@/hooks/api/dashboard'
import { AlbumGrid } from '@/features/album/components/AlbumGrid'
import { GalleryGrid } from '@/features/album/components/GalleryGrid'
import { PersonRow } from '@/features/connections/components/PersonRow'
import { PersonActionButtons } from '@/features/connections/components/PersonActionButtons'
import { CirclePickerModal } from '@/features/connections/components/CirclePickerModal'
import { useConnectionRequestActions } from '@/features/connections/hooks/useConnectionRequestActions'
import { useGlobalSearch } from '../../hooks/useGlobalSearch'

type ResultTab = 'albums' | 'galleries' | 'people'

const Page = styled(Box)({ maxWidth: 1040 })
const PageHead = styled(Box)({ marginBottom: 4 })
const H1 = styled(Typography)({ fontSize: 24, fontWeight: 720, letterSpacing: '-0.03em', color: 'var(--ds-ink)' })
const Sub = styled(Typography)({ fontSize: 13, color: 'var(--ds-ink-subtle)', marginTop: 5 })
const TabRow = styled(Box)({ padding: '22px 0' })

const ListCard = styled(Card)({
  padding: 0,
  overflow: 'hidden',
  background: 'var(--ds-canvas)',
  boxShadow: 'var(--ds-shadow-soft)',
  '& > *:not(:first-of-type)': { borderTop: '1px solid var(--ds-border)' },
})

const SkeletonRow = styled(Box)({ display: 'flex', alignItems: 'center', gap: 14, padding: '13px 18px' })

function RowListSkeleton({ count = 4 }: { count?: number }) {
  return (
    <ListCard>
      {Array.from({ length: count }).map((_, i) => (
        <SkeletonRow key={i}>
          <Skeleton width={46} height={46} rounded='pill' />
          <Box sx={{ flex: 1 }}>
            <Skeleton width={i % 2 === 0 ? 160 : 130} height={13} rounded='sm' sx={{ mb: 1 }} />
            <Skeleton width={200} height={11} rounded='sm' />
          </Box>
        </SkeletonRow>
      ))}
    </ListCard>
  )
}

/**
 * `/search` — the destination for the shell top-bar's Enter-to-search
 * (see AlbumShellLayout), and a shareable link on its own (`/search?key=…`).
 * Three tabs over one `search/search` request pair: Albums + Galleries (split
 * client-side from `useSearchTopics`' topic list by `topicType`) and People
 * (`useSearchUsers`). People actions reuse the same accept/decline/connect
 * wiring as `/connections` via `useConnectionRequestActions`.
 */
export function SearchResultsScreen() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const key = searchParams.get('key') || ''
  const [tab, setTab] = useState<ResultTab>('albums')

  const { albums, galleries, people, loading, hasSearched } = useGlobalSearch(key)

  useEffect(() => {
    setTab('albums')
  }, [key])

  const [connRequests, myCircles, setConnRequests] = useAppStore(
    (state) => [state.user.connRequests, state.user.myGroups, state.setConnRequests],
    shallow,
  )
  const getConnectRequests = useGetConnectRequests()
  const getMyCircles = useGetMyCircles()

  useEffect(() => {
    // Requests + circles aren't guaranteed to be loaded yet — unlike /connections,
    // people can land here (via Enter from any page) without ever visiting
    // /connections first this session.
    getConnectRequests.mutate()
    getMyCircles.mutate()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const systemCircles = useMemo(() => myCircles.filter((c) => c.isSystemProvided === '1'), [myCircles])

  const {
    circlePicker,
    openAccept,
    openConnect,
    closeCirclePicker,
    onSubmitCircle,
    onDecline,
    onCancelSent,
    decliningID,
    cancellingID,
    sentIDs,
    accepting,
    connecting,
  } = useConnectionRequestActions({
    onAccepted: (connReqID) => {
      setConnRequests(connRequests.filter((r) => r.connReqID !== connReqID))
      getConnectRequests.mutate()
    },
    onDeclined: (connReqID) => {
      setConnRequests(connRequests.filter((r) => r.connReqID !== connReqID))
      getConnectRequests.mutate()
    },
  })

  const tabOptions = [
    { value: 'albums', label: `Albums ${albums.length}` },
    { value: 'galleries', label: `Galleries ${galleries.length}` },
    { value: 'people', label: `People ${people.length}` },
  ]

  const total = albums.length + galleries.length + people.length

  return (
    <Page>
      <PageHead>
        <H1>Search</H1>
        <Sub>
          {!hasSearched ? (
            'What are you looking for?'
          ) : loading ? (
            'Searching…'
          ) : (
            <>
              {total} result{total === 1 ? '' : 's'} for &ldquo;{key}&rdquo;
            </>
          )}
        </Sub>
      </PageHead>

      {hasSearched && (
        <TabRow>
          <ChipToggleGroup options={tabOptions} value={tab} onChange={(v) => setTab(v as ResultTab)} />
        </TabRow>
      )}

      {!hasSearched ? (
        <EmptyState
          icon={<span style={{ fontSize: 34, opacity: 0.25 }}>⌕</span>}
          title='Search across everything'
          description='Type an album title, a gallery name, or a person’s name in the bar above to get started.'
        />
      ) : tab === 'albums' ? (
        !loading && albums.length === 0 ? (
          <EmptyState
            icon={<span style={{ fontSize: 32, opacity: 0.35 }}>▤</span>}
            title='No matching albums'
            description={`Nothing titled like "${key}" in your albums.`}
          />
        ) : (
          <AlbumGrid albums={albums} loading={loading} showNewTile={false} onSelect={(a) => navigate(`/albums/${a.id}`)} />
        )
      ) : tab === 'galleries' ? (
        !loading && galleries.length === 0 ? (
          <EmptyState
            icon={<span style={{ fontSize: 32, opacity: 0.35 }}>◈</span>}
            title='No matching galleries'
            description={`Nothing titled like "${key}" in your 3D galleries.`}
          />
        ) : (
          <GalleryGrid
            galleries={galleries}
            loading={loading}
            view='grid'
            showNewTile={false}
            onSelect={(g) => navigate(`/galleries/${g.id}/studio`)}
          />
        )
      ) : loading ? (
        <RowListSkeleton />
      ) : people.length === 0 ? (
        <EmptyState
          icon={<span style={{ fontSize: 32, opacity: 0.35 }}>◎</span>}
          title='No matching people'
          description={`Nobody named or described like "${key}" in your connections.`}
        />
      ) : (
        <ListCard>
          {people.map((result) => {
            const isConnected = result.isConnection
            const isSent = sentIDs.has(result.userID) || result.reqSent
            const isReceived = Boolean(result.reqReceived)
            const pendingReqID = connRequests.find((r) => r.requestorID === result.userID)?.connReqID

            return (
              <PersonRow
                key={result.userID}
                avatarUrl={result.profilePicThumbURL || result.profilePicURL}
                name={`${result.firstName} ${result.lastName}`}
                profession={result.profession}
                location={result.location}
                onClick={() => navigate(`/profile/${result.userID}`)}
                actions={
                  <PersonActionButtons
                    state={isConnected ? 'connected' : isSent ? 'sent' : isReceived && pendingReqID ? 'received' : 'none'}
                    busy={decliningID === pendingReqID}
                    cancelling={cancellingID === result.userID}
                    onDecline={() => pendingReqID && onDecline(pendingReqID)}
                    onAccept={() => pendingReqID && openAccept(pendingReqID, `${result.firstName} ${result.lastName}`)}
                    onConnect={() =>
                      openConnect({
                        userID: result.userID,
                        name: `${result.firstName} ${result.lastName}`,
                        profession: result.profession,
                        location: result.location,
                        avatarUrl: result.profilePicThumbURL || result.profilePicURL,
                      })
                    }
                    onCancelSent={() => onCancelSent(result.userID)}
                  />
                }
              />
            )
          })}
        </ListCard>
      )}

      <CirclePickerModal
        open={Boolean(circlePicker)}
        onClose={closeCirclePicker}
        personName={circlePicker?.name ?? ''}
        submitLabel={circlePicker?.mode === 'accept' ? 'Accept' : 'Send request'}
        circles={systemCircles}
        loading={accepting || connecting}
        onSubmit={onSubmitCircle}
      />
    </Page>
  )
}
